import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import Tooltip from "@hyro-components/Tooltip";
import palette from "src/theme/palette";
import {
  ALL_CHANNELS_USAGE_LABEL,
  buildPlanSubtitle,
  CHANNEL_META,
  QUOTA_NOT_STARTED_SUBTITLE,
  SKILLS_TOOLTIP_STYLE,
  UNLIMITED_LIMIT_LABEL,
  UNLIMITED_PLAN_SUBTITLE,
  USAGE_BAR_MIN_SEGMENT_PX
} from "./SubscriptionTab.consts";
import { Track } from "./QuotaUsageSummary.styles";
import ChannelLegend from "./ChannelLegend";
import ChannelSkillsTooltipContent from "./ChannelSkillsTooltipContent";

const channelValue = (entry) => entry?.engaged ?? 0;

const QuotaUsageSummary = ({ term, interactionLimit, isNotStarted, skillsByChannel }) => {
  const safeLimit = interactionLimit ?? 0;
  const isUnlimited = !interactionLimit;
  const usageByChannel = Object.fromEntries((term.usage ?? []).map((entry) => [entry.channelType, entry]));

  const channels = Object.entries(CHANNEL_META).map(([channelType, meta]) => ({
    channelType,
    value: channelValue(usageByChannel[channelType]),
    meta
  }));
  const usedTotal = channels.reduce((sum, c) => sum + c.value, 0);
  const isOverLimit = !isUnlimited && usedTotal > safeLimit;
  const usedColor = isOverLimit ? palette.error[500] : palette.text[800];

  if (isNotStarted) {
    return (
      <Stack alignItems="center" gap={1} sx={{ py: 5 }} data-testid="quota-not-started">
        <Typography variant="body1" color="text.700">Nothing here yet.</Typography>
        <Typography variant="body2" color="text.600" textAlign="center" maxWidth={320}>
          {QUOTA_NOT_STARTED_SUBTITLE}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack gap="18px" data-testid="quota-usage-summary">
      <Stack direction="row" alignItems="flex-end" gap="12px">
        <Stack direction="row" alignItems="center" gap="6px">
          <Typography
            variant="h5"
            sx={{ color: usedColor }}
            data-testid="quota-used-total"
            data-over-limit={isOverLimit ? "true" : undefined}
          >
            {usedTotal.toLocaleString()}
          </Typography>
          <Typography variant="h6" sx={{ color: palette.text[800] }}>
            /
          </Typography>
          <Typography variant="h5" sx={{ color: palette.text[700] }}>
            {isUnlimited ? UNLIMITED_LIMIT_LABEL : safeLimit.toLocaleString()}
          </Typography>
        </Stack>
        <Typography variant="subtitle2" sx={{ color: palette.text[700], lineHeight: "22px" }}>
          {ALL_CHANNELS_USAGE_LABEL}
        </Typography>
      </Stack>

      <Track>
        {usedTotal > 0 &&
          channels.map((c) =>
            c.value > 0 ? (
              <Tooltip
                key={c.channelType}
                title={
                  <ChannelSkillsTooltipContent
                    channelType={c.channelType}
                    engaged={c.value}
                    skills={skillsByChannel?.[c.channelType]}
                  />
                }
                variant="light"
                placement="top"
                tooltipStyle={SKILLS_TOOLTIP_STYLE}
                stackStyle={{
                  flex: `${c.value} 1 0`,
                  minWidth: `${USAGE_BAR_MIN_SEGMENT_PX}px`,
                  height: "100%",
                  bgcolor: c.meta.color
                }}
              >
                <Box data-testid={`quota-bar-${c.channelType}`} sx={{ width: "100%", height: "100%" }} />
              </Tooltip>
            ) : null
          )}
        {safeLimit > usedTotal && (
          <Box
            data-testid="quota-bar-remaining"
            sx={{ flex: `${safeLimit - usedTotal} 1 0`, height: "100%" }}
          />
        )}
      </Track>

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" sx={{ color: palette.text[700], lineHeight: "22px" }}>
          {isUnlimited ? UNLIMITED_PLAN_SUBTITLE : buildPlanSubtitle(safeLimit)}
        </Typography>
        <ChannelLegend />
      </Stack>
    </Stack>
  );
};

export default QuotaUsageSummary;
